import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Flex, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'

import useAuthStore from 'Store/useAuthStore'
import { getJobRequisitionView, getEmployeeFunctionAccess, updateEmployeeFunctionAccess, transferPAICProjects } from 'Api/apiService'

import { SbTable, SbButton, SbTag, SbModal, SbForm, SbSelect, SbSwitch, SbNotification, EditOutlined, ReloadOutlined, BUTTON_TYPE, SbTooltip, TooltipTriggerStyled } from 'uiCore'
import { buildLegalEntityOptions, buildOwnerOptions, getAllOrgValues, getChainedOptions, getOwnershipConflict, useRoleIdOptions } from './Helper'
import { expandSentinels, filterSelected, isSelectAll } from 'Components/SbJdCreationDrawerV2/helpers'
import OwnershipConflictModal from './OwnershipConflictModal'
import { getTransferErrorMessage, watchTransferTask } from './watchTransferTask'
import { handlePAICErrorCode } from 'constants/peoplePlusAI.constants'
import { ACCESS_TOKEN_KEY } from 'constants/localStorage.constants'
import { JD_STATUSES } from 'constants/jobs.constants'
import Permission from 'Components/Permission'
import {
  PageContainerStyled,
  TableWrapperStyled,
  PageTableWrapperStyled,
} from 'styles/common.styles'
import { SettingsActionButtonStyled, SettingsModalFooterStyled, SettingsModalFormWrapperStyled } from './EmployeeFunctionAccess.styles'
import PageHeader from 'Components/PageHeader'
import { toFilterOptions } from 'Pages/TalentMobilityV2/RedeploymentCenterV2/RedeploymentCenterV2.utils'

const TOOLTIP_SAVE_ACCESS = "Save the updated function access settings for this employee.";

const { Title, Text } = Typography

const getFullName = (record) =>
  `${record.userdata?.first_name ?? ''} ${record.userdata?.last_name ?? ''}`.trim()

const EmployeeFunctionAccess = () => {
  const navigate = useNavigate()

  const [form] = SbForm.useForm()

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [employeeList, setEmployeeList] = useState([])
  const [isTableLoading, setIsTableLoading] = useState(false)

  // Selections captured just before Full Org was switched on, so it can be undone
  const preFullOrgSelectionRef = useRef(null)

  // { message, projects } from a 409/7214 save; the rejected payload is kept so it
  // can be resent unchanged once the blocking projects are transferred
  const [ownershipConflict, setOwnershipConflict] = useState(null)
  const [isTransferring, setIsTransferring] = useState(false)
  const [transferStatusText, setTransferStatusText] = useState('')
  const pendingAccessPayloadRef = useRef(null)
  // Guards against overlapping save/transfer requests from rapid clicks
  const isRequestInFlightRef = useRef(false)
  const cancelTransferWatchRef = useRef(null)
  // Stops a transfer that is still in flight from continuing after the page is left
  const isUnmountedRef = useRef(false)

  useEffect(() => {
    isUnmountedRef.current = false
    return () => {
      isUnmountedRef.current = true
      cancelTransferWatchRef.current?.()
    }
  }, [])

  // ── Store ─────────────────────────────────────────────────────────────────────
  const email = useAuthStore((state) => state.email)
  const accessToken = useAuthStore((state) => state.accessToken) || localStorage.getItem(ACCESS_TOKEN_KEY)
  const orgMetaData = useAuthStore((state) => state.orgMetaData)
  const [requisitionViewData, setRequisitionViewData] = useState([])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing on dev, not changed by this PR
  const structure = orgMetaData?.organization_structure ?? []

  // Fetch published JDs only. The list is the Jobs Library requisition view, which now returns each JD's
  // newest version in any status (the Latest JD service's LATEST_ANY), so the live JDs are the rows whose
  // version is PUBLISHED. The mode decided for assigning a JD to a person is LATEST_PUBLISHED (a JD taken
  // down for editing stays assignable); this screen moves onto it with the other downstream callers
  // in rollout step 5, so until then it keeps this published-only filter.
  const loadRequisitions = async () => {
    try {
      // view_all=true: admins with function-access write access see every published
      // JD org-wide (not just their own function scope) when assigning Role IDs.
      const data = await getJobRequisitionView(email, true)
      const list = Array.isArray(data) ? data : data?.data ?? data?.requisitions ?? []
      setRequisitionViewData(list.filter((r) => r.jd_status === JD_STATUSES.PUBLISHED))
    } catch (err) {
      console.error('EmployeeFunctionAccess | failed to load requisitions:', err)
    }
  }

  useEffect(() => {
    loadRequisitions()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reactive form values (drive disabled states, rules, and cascading options) ─
  const roleId = SbForm.useWatch('roleId', form)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing on dev, not changed by this PR
  const legalEntities = SbForm.useWatch('legalEntities', form) ?? []
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing on dev, not changed by this PR
  const businessUnits = SbForm.useWatch('businessUnits', form) ?? []
  const functionId = SbForm.useWatch('functionId', form)
  const isFullOrg = SbForm.useWatch('fullOrg', form) ?? false

  // ── Derived options ───────────────────────────────────────────────────────────
  const roleIdOptions = useRoleIdOptions(requisitionViewData, orgMetaData)

  const ownerOptions = useMemo(
    () => buildOwnerOptions(employeeList, selectedEmployee),
    [employeeList, selectedEmployee]
  )

  const legalEntityOptions = useMemo(() => buildLegalEntityOptions(structure), [structure])

  // Pass 1 — BU + Function options
  const { businessUnitOptions, functionOptions } = useMemo(
    () => getChainedOptions(structure, {
      legalEntities,
      businessUnits,
      functions: [],
    }),
    [structure, legalEntities, businessUnits]
  )

  // Expand sentinels before drilling into sub-functions
  const resolvedFunctionValues = useMemo(
    () => expandSentinels(functionId ?? [], functionOptions),
    [functionId, functionOptions]
  )

  // Pass 2 — Sub-function options. If expansion yields no real subs but a sentinel
  // function is selected, mirror the sentinel so user can still pick (saved as blank).
  const subFunctionOptions = useMemo(() => {
    const { subFunctionOptions: raw } = getChainedOptions(structure, {
      legalEntities,
      businessUnits,
      functions: resolvedFunctionValues,
    })
    if (raw.length) return raw
    const sentinel = (functionId ?? []).find(isSelectAll)
    return sentinel ? [{ value: sentinel, label: sentinel }] : []
  }, [structure, legalEntities, businessUnits, resolvedFunctionValues, functionId])


  const loadEmployeeList = async () => {
    setIsTableLoading(true)
    try {
      const data = await getEmployeeFunctionAccess(email)
      setEmployeeList(data?.users ?? [])
    } catch (err) {
      console.error('EmployeeFunctionAccess | failed to load employee list:', err)
    } finally {
      setIsTableLoading(false)
    }
  }

  useEffect(() => {
    loadEmployeeList()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cascade handlers ──────────────────────────────────────────────────────────

  const handleRoleIdChange = (selectedRoleId) => {
    const req = requisitionViewData.find((r) => r.sb_requisition_number === selectedRoleId)

    form.setFieldsValue({
      legalEntities: req?.legal_entity ? [req.legal_entity] : [],
      businessUnits: req?.business_unit ? [req.business_unit] : [],
      functionId: req?.function ? [req.function] : [],
      subFunction: req?.sub_function ? [req.sub_function] : [],
    })
  }

  const handleLegalEntitiesChange = (allValues) => {
    const expandedLE = expandSentinels(allValues.legalEntities ?? [], legalEntityOptions)

    const { businessUnitOptions: newBUOpts } = getChainedOptions(structure, { legalEntities: expandedLE, businessUnits: [], functions: [] })
    const filteredBUs = filterSelected(allValues.businessUnits, newBUOpts)

    const { functionOptions: newFuncOpts } = getChainedOptions(structure, { legalEntities: expandedLE, businessUnits: filteredBUs, functions: [] })
    const filteredFns = filterSelected(allValues.functionId ?? [], newFuncOpts)

    const { subFunctionOptions: newSubOpts } = getChainedOptions(structure, {
      legalEntities: expandedLE,
      businessUnits: filteredBUs,
      functions: expandSentinels(filteredFns, newFuncOpts),
    })

    form.setFieldsValue({
      legalEntities: expandedLE,
      businessUnits: filteredBUs,
      functionId: filteredFns,
      subFunction: filterSelected(allValues.subFunction ?? [], newSubOpts),
    })
  }

  const handleBusinessUnitsChange = (allValues) => {
    const newLE = allValues.legalEntities ?? []
    const { businessUnitOptions: newBUOpts } = getChainedOptions(structure, { legalEntities: newLE, businessUnits: [], functions: [] })
    const expandedBU = expandSentinels(allValues.businessUnits ?? [], newBUOpts)

    const { functionOptions: newFuncOpts } = getChainedOptions(structure, { legalEntities: newLE, businessUnits: expandedBU, functions: [] })
    const filteredFns = filterSelected(allValues.functionId ?? [], newFuncOpts)

    const { subFunctionOptions: newSubOpts } = getChainedOptions(structure, {
      legalEntities: newLE,
      businessUnits: expandedBU,
      functions: expandSentinels(filteredFns, newFuncOpts),
    })

    form.setFieldsValue({
      businessUnits: expandedBU,
      functionId: filteredFns,
      subFunction: filterSelected(allValues.subFunction ?? [], newSubOpts),
    })
  }

  const handleFunctionIdChange = (allValues) => {
    const newLE = allValues.legalEntities ?? []
    const newBU = allValues.businessUnits ?? []

    const { functionOptions: newFuncOpts } = getChainedOptions(structure, { legalEntities: newLE, businessUnits: newBU, functions: [] })
    const expandedFns = expandSentinels(allValues.functionId ?? [], newFuncOpts)

    const { subFunctionOptions: newSubOpts } = getChainedOptions(structure, {
      legalEntities: newLE,
      businessUnits: newBU,
      functions: expandedFns,
    })

    form.setFieldsValue({
      functionId: expandedFns,
      subFunction: filterSelected(allValues.subFunction ?? [], newSubOpts),
    })
  }

  // Full Org mirrors the whole org structure into the four selects straight away,
  // so what the user sees matches what gets saved. The pre-toggle selection is
  // stashed so switching back off restores it instead of wiping their work.
  const handleFullOrgChange = (enabled, allValues) => {
    if (enabled) {
      preFullOrgSelectionRef.current = {
        legalEntities: allValues.legalEntities ?? [],
        businessUnits: allValues.businessUnits ?? [],
        functionId: allValues.functionId ?? [],
        subFunction: allValues.subFunction ?? [],
      }

      const all = getAllOrgValues(structure)
      form.setFieldsValue({
        legalEntities: all.legal_entities,
        businessUnits: all.business_units,
        functionId: all.functions,
        subFunction: all.sub_functions,
      })
      return
    }

    form.setFieldsValue(
      preFullOrgSelectionRef.current ?? {
        legalEntities: [],
        businessUnits: [],
        functionId: [],
        subFunction: [],
      }
    )
    preFullOrgSelectionRef.current = null
  }

  const handleSubFunctionChange = (allValues) => {
    const newLE = allValues.legalEntities ?? []
    const newBU = allValues.businessUnits ?? []
    const newFns = allValues.functionId ?? []

    const { functionOptions: newFuncOpts } = getChainedOptions(structure, { legalEntities: newLE, businessUnits: newBU, functions: [] })
    const { subFunctionOptions: newSubOpts } = getChainedOptions(structure, {
      legalEntities: newLE,
      businessUnits: newBU,
      functions: expandSentinels(newFns, newFuncOpts),
    })

    form.setFieldsValue({
      subFunction: expandSentinels(allValues.subFunction ?? [], newSubOpts),
    })
  }

  // ── onValuesChange — dispatcher for cascade handlers ────────────────────────

  const onValuesChange = (changedValues, allValues) => {
    if ('fullOrg' in changedValues) {
      handleFullOrgChange(changedValues.fullOrg, allValues)
    } else if ('roleId' in changedValues) {
      handleRoleIdChange(changedValues.roleId)
    } else if ('legalEntities' in changedValues) {
      if ('functionId' in changedValues) return
      handleLegalEntitiesChange(allValues)
    } else if ('businessUnits' in changedValues) {
      if ('functionId' in changedValues) return
      handleBusinessUnitsChange(allValues)
    } else if ('functionId' in changedValues) {
      if ('subFunction' in changedValues) return
      handleFunctionIdChange(allValues)
    } else if ('subFunction' in changedValues) {
      handleSubFunctionChange(allValues)
    }
  }

  // ── Modal handlers ────────────────────────────────────────────────────────────

  // useCallback so the memoized table columns (whose Edit cells close over
  // this) only rebuild when the employee list changes, not on every render.
  const handleOpenSettingsModal = useCallback((employee) => {
    const access = employee.function_access ?? {}
    setSelectedEmployee(employee)
    setSettingsModalOpen(true)

    const savedLegalEntities = access.legal_entities ?? []
    const savedBusinessUnits = access.business_units ?? []
    const savedFunctions = access.functions ?? []
    const savedSubFunctions = access.sub_functions ?? []

    form.setFieldsValue({
      roleId: employee.jd_assignment?.sb_requisition_number ?? undefined,
      legalEntities: savedLegalEntities,
      businessUnits: savedBusinessUnits,
      functionId: savedFunctions,
      subFunction: savedSubFunctions,
      fullOrg: access.full_org_access ?? false,
    })
  }, [form])

  const clearOwnershipConflict = () => {
    setOwnershipConflict(null)
    pendingAccessPayloadRef.current = null
  }

  const handleCloseSettingsModal = () => {
    setSettingsModalOpen(false)
    setSelectedEmployee(null)
    preFullOrgSelectionRef.current = null
    clearOwnershipConflict()
    form.resetFields()
  }

  // ── Save ──────────────────────────────────────────────────────────────────────

  // Sends the access update and handles every outcome. Returns true on success.
  // A 409/7214 opens (or refreshes) the Ownership Conflict modal instead of erroring.
  const submitAccessUpdate = async (payload, { afterTransfer = false } = {}) => {
    try {
      await updateEmployeeFunctionAccess(payload)
    } catch (error) {
      const conflict = getOwnershipConflict(error)

      if (conflict?.projects.length) {
        pendingAccessPayloadRef.current = payload
        setOwnershipConflict(conflict)
        if (afterTransfer) {
          SbNotification.warning('Ownership conflict remains', 'Other PAIC projects owned by this user still block this change. Transfer them to continue.')
        }
        return false
      }

      clearOwnershipConflict()
      if (conflict) {
        // 7214 without usable project details — nothing to transfer from here
        SbNotification.error('Ownership conflict', conflict.message || 'Failed to update employee access')
      } else if (afterTransfer) {
        SbNotification.error('Error', 'Projects were transferred, but the employee access update failed. Click Save to try again.')
      } else {
        SbNotification.error('Error', 'Failed to update employee access')
      }
      return false
    }

    SbNotification.success(
      'Success',
      afterTransfer ? 'PAIC projects transferred and employee access updated successfully' : 'Employee access updated successfully'
    )
    handleCloseSettingsModal()
    loadEmployeeList()
    return true
  }

  // Resolves true once the transfer task completes; otherwise notifies and resolves false
  const waitForTransferTask = async (taskId) => {
    setTransferStatusText('Transferring projects...')
    const { promise, cancel } = watchTransferTask(taskId, {
      accessToken,
      onProgress: ({ progress }) => {
        const percent = Number(progress)
        setTransferStatusText(percent > 0 ? `Transferring projects... ${Math.min(percent, 99)}%` : 'Transferring projects...')
      },
    })
    cancelTransferWatchRef.current = cancel
    const { outcome, payload: taskEvent } = await promise
    cancelTransferWatchRef.current = null

    if (outcome === 'completed') return true
    if (outcome === 'failed') {
      SbNotification.error('Transfer Failed', getTransferErrorMessage(taskEvent) || 'The project transfer failed. Please try again.')
    } else if (outcome === 'unavailable') {
      // Ownership may or may not have moved — send the admin back to Save rather than guess
      clearOwnershipConflict()
      SbNotification.error('Transfer status unknown', "Couldn't confirm the project transfer finished. Check the PAIC groups, then click Save again.")
    }
    return false
  }

  const handleTransferAndRetry = async (newOwnerEmail) => {
    const payload = pendingAccessPayloadRef.current
    const projectIds = ownershipConflict?.projects.map((project) => project.sb_project_unique_id) ?? []
    if (!payload || !projectIds.length || !newOwnerEmail || isRequestInFlightRef.current) return

    isRequestInFlightRef.current = true
    setIsTransferring(true)
    setTransferStatusText('Starting transfer...')
    try {
      let transferResponse
      try {
        transferResponse = await transferPAICProjects(projectIds, newOwnerEmail)
      } catch (error) {
        if (!handlePAICErrorCode(error, SbNotification.error)) {
          SbNotification.error('Transfer Failed', 'Unable to transfer project ownership. Please try again.')
        }
        return
      }
      if (isUnmountedRef.current) return

      // The transfer is queued, so retrying before the task finishes would hit 7214 again
      const taskId = transferResponse?.task_id
      if (taskId) {
        const transferred = await waitForTransferTask(taskId)
        if (!transferred) return
      }

      setTransferStatusText('Saving access changes...')
      // Single retry — a repeat 7214 just refreshes the modal for another explicit transfer
      await submitAccessUpdate(payload, { afterTransfer: true })
    } finally {
      isRequestInFlightRef.current = false
      setIsTransferring(false)
      setTransferStatusText('')
    }
  }

  const handleCancelOwnershipConflict = () => {
    if (isRequestInFlightRef.current) return
    clearOwnershipConflict()
  }

  const handleSaveSettings = async (values) => {
    if (isRequestInFlightRef.current) return

    const { roleId: selectedRoleId, legalEntities: selectedLE, businessUnits: selectedBU, functionId: selectedFunc, subFunction, fullOrg } = values
    const selectedReq = requisitionViewData.find((r) => r.sb_requisition_number === selectedRoleId)

    const payload = {
      sb_user_unique_id: selectedEmployee?.userdata?.sb_user_unique_id,
      linked_jd_req_number: selectedReq?.sb_requisition_number ?? null,
      jd_sb_unique_id: selectedReq?.sb_unique_id ?? null,
      full_org_access: fullOrg,
      ...(fullOrg
        ? getAllOrgValues(structure)
        : {
          legal_entities: selectedLE ?? [],
          business_units: selectedBU ?? [],
          functions: expandSentinels(selectedFunc ?? [], functionOptions).filter((v) => !isSelectAll(v)),
          sub_functions: expandSentinels(subFunction ?? [], subFunctionOptions).filter((v) => !isSelectAll(v)),
        }),
    }
    isRequestInFlightRef.current = true
    setIsTableLoading(true)
    try {
      // On success loadEmployeeList owns the loading flag from here
      const succeeded = await submitAccessUpdate(payload)
      if (!succeeded) setIsTableLoading(false)
    } finally {
      isRequestInFlightRef.current = false
    }
  }

  const handleRefresh = () => {
    loadEmployeeList()
  }

  // ── Table config ──────────────────────────────────────────────────────────────

  // All three filterable columns render derived values (full name, nested
  // email, roles array), so options are built from the same derivations the
  // cells show — memoized, since rebuilding them walks the full employee
  // list and this component re-renders on every modal-form keystroke.
  const tableColumns = useMemo(() => [
    {
      title: 'Name',
      key: 'name',
      width: 380,
      filters: toFilterOptions(employeeList.map(getFullName)),
      filterSearch: true,
      onFilter: (value, record) => getFullName(record) === value,
      render: (_, record) => getFullName(record),
    },
    {
      title: 'Email',
      key: 'email',
      filters: toFilterOptions(employeeList.map((record) => record.userdata?.email)),
      filterSearch: true,
      onFilter: (value, record) => record.userdata?.email === value,
      render: (_, record) => <Text>{record.userdata?.email}</Text>,
    },
    {
      title: 'Core Profiles',
      key: 'coreEntitlements',
      width: 380,
      // One option per distinct profile; a row matches when it holds the
      // selected profile (rows carry several).
      filters: toFilterOptions(employeeList.flatMap((record) => record.profile?.roles ?? [])),
      filterSearch: true,
      onFilter: (value, record) => (record.profile?.roles ?? []).includes(value),
      render: (_, record) => {
        const roles = record.profile?.roles ?? []
        return (
          <Flex wrap gap={4}>
            {roles.map((role) => (
              <SbTag key={role} color="processing">{role}</SbTag>
            ))}
          </Flex>
        )
      },
    },
    {
      title: 'Edit Function Access',
      key: 'editFunctionAccess',
      align: 'center',
      onCell: (record) => ({
        onClick: () => handleOpenSettingsModal(record),
        style: { cursor: 'pointer' },
      }),
      render: () => (
        <SettingsActionButtonStyled buttonType="action" icon={<EditOutlined />} />
      ),
    },
  ], [employeeList, handleOpenSettingsModal])

  const modalFooter = (
    <SettingsModalFooterStyled>
      <SbButton buttonType="secondary" onClick={handleCloseSettingsModal} disabled={isTableLoading}>Cancel</SbButton>
      <Permission resource="user_access_function" actions={["edit"]}>
        <SbTooltip content={TOOLTIP_SAVE_ACCESS}>
          <TooltipTriggerStyled>
            <SbButton buttonType="primary" onClick={() => form.submit()} loading={isTableLoading}>Save</SbButton>
          </TooltipTriggerStyled>
        </SbTooltip>
      </Permission>
    </SettingsModalFooterStyled>
  )

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <PageContainerStyled>
        <PageHeader
          title="Function Access Control"
          onBack={() => navigate('/admin-setup')}
          backLabel="Back to Admin Setup"
          actions={
            <SbButton buttonType={BUTTON_TYPE.SECONDARY} icon={<ReloadOutlined />} onClick={handleRefresh}>
              Refresh
            </SbButton>
          }
        />

        <TableWrapperStyled>
          <PageTableWrapperStyled>
            <SbTable
              columns={tableColumns}
              dataSource={employeeList}
              rowKey="user_id"
              loading={isTableLoading}
              sticky
              pagination={{
                showTotal: (total) => (
                  <div className="paic-pagination-total">
                    <span className="is-active-text">{`${total} Employees`}</span>
                  </div>
                ),
              }}
            />
          </PageTableWrapperStyled>
        </TableWrapperStyled>
      </PageContainerStyled>

      <SbModal
        open={settingsModalOpen}
        title={<Title level={5}>Update {`${selectedEmployee?.userdata?.first_name ?? ''} ${selectedEmployee?.userdata?.last_name ?? ''}`.trim()} Data</Title>}
        onCancel={handleCloseSettingsModal}
        footer={modalFooter}
        centered
        width={520}
        destroyOnClose
      >
        <SettingsModalFormWrapperStyled>
          <SbForm form={form} layout="vertical" onFinish={handleSaveSettings} onValuesChange={onValuesChange}>

            <SbForm.Item
              label="Role ID"
              name="roleId"
              rules={[{ required: true, message: 'Role ID is required' }]}
              extra={roleId ? 'JD already assigned — cannot be changed.' : undefined}
            >
              <SbSelect
                placeholder="Select Role ID"
                options={roleIdOptions}
                showSearch
                optionFilterProp="label"
                allowClear
                disabled={!!roleId}
              />
            </SbForm.Item>

            <SbForm.Item
              label="Legal Entity"
              name="legalEntities"
              rules={isFullOrg ? [] : [{ required: true, message: 'Legal Entity is required' }]}
            >
              <SbSelect
                placeholder="Select Legal Entity"
                mode="multiple"
                options={legalEntityOptions}
                disabled={!roleId || isFullOrg}
              />
            </SbForm.Item>

            <SbForm.Item
              label="Business Unit"
              name="businessUnits"
              rules={isFullOrg ? [] : [{ required: true, message: 'Business Unit is required' }]}
            >
              <SbSelect
                placeholder="Select Business Unit"
                mode="multiple"
                options={businessUnitOptions}
                disabled={!legalEntities.length || isFullOrg}
              />
            </SbForm.Item>

            <SbForm.Item
              label="Function"
              name="functionId"
              rules={isFullOrg ? [] : [{ required: true, message: 'Function is required' }]}
            >
              <SbSelect
                placeholder="Select Function"
                mode="multiple"
                options={functionOptions}
                disabled={!businessUnits.length || isFullOrg}
              />
            </SbForm.Item>

            <SbForm.Item
              label="Sub-Function"
              name="subFunction"
              rules={isFullOrg ? [] : [{ required: true, message: 'Sub-Function is required' }]}
            >
              <SbSelect
                placeholder="Select Sub-Function"
                mode="multiple"
                options={subFunctionOptions}
                disabled={!functionId?.length || isFullOrg}
              />
            </SbForm.Item>

            <SbForm.Item label="Full Org" name="fullOrg" valuePropName="checked">
              <SbSwitch disabled={!roleId} />
            </SbForm.Item>

          </SbForm>
        </SettingsModalFormWrapperStyled>
      </SbModal>

      <OwnershipConflictModal
        open={Boolean(ownershipConflict)}
        message={ownershipConflict?.message}
        projects={ownershipConflict?.projects}
        ownerOptions={ownerOptions}
        isTransferring={isTransferring}
        transferStatusText={transferStatusText}
        onTransfer={handleTransferAndRetry}
        onCancel={handleCancelOwnershipConflict}
      />
    </>


  )
}

export default EmployeeFunctionAccess

